<script setup lang="ts">
import { useI18n } from "vue-i18n";
import MemberHeader from "@/components/member/MemberHeader.vue";

const { t } = useI18n();
</script>

<template>
  <MemberHeader />

  <main id="main-content">
    <section
      id="hero-section"
      class="bg-gradient-to-br from-primary to-accent text-white h-[300px] flex items-center"
    >
      <div class="max-w-7xl mx-auto px-5 w-full">
        <div class="text-center">
          <h1 class="text-4xl font-bold mb-4">{{ $t('help.help_page.hero.title') }}</h1>
          <p class="text-xl text-gray-200 mb-6">
            {{ $t('help.help_page.hero.subtitle') }}
          </p>
          <div class="max-w-md mx-auto">
            <div class="relative">
              <input
                type="text"
                :placeholder="$t('help.help_page.hero.search_placeholder')"
                class="w-full px-4 py-3 pl-12 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white"
              />
              <i
                class="fas fa-search absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"
              ></i>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="quick-access" class="py-12">
      <div class="max-w-7xl mx-auto px-5">
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          <div
            id="faq-card"
            class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div
              class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4"
            >
              <i class="fas fa-question-circle text-primary text-xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('help.help_page.quick_access.faq.title') }}</h3>
            <p class="text-gray-600 text-sm mb-4">
              {{ $t('help.help_page.quick_access.faq.description') }}
            </p>
            <div class="text-sm text-gray-500">{{ $t('help.help_page.quick_access.faq.count') }}</div>
          </div>

          <div
            id="templates-card"
            class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div
              class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4"
            >
              <i class="fas fa-download text-secondary text-xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('help.help_page.quick_access.templates.title') }}</h3>
            <p class="text-gray-600 text-sm mb-4">
              {{ $t('help.help_page.quick_access.templates.description') }}
            </p>
            <div class="text-sm text-gray-500">{{ $t('help.help_page.quick_access.templates.count') }}</div>
          </div>

          <div
            id="guides-card"
            class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div
              class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4"
            >
              <i class="fas fa-book text-accent text-xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('help.help_page.quick_access.guides.title') }}</h3>
            <p class="text-gray-600 text-sm mb-4">
              {{ $t('help.help_page.quick_access.guides.description') }}
            </p>
            <div class="text-sm text-gray-500">{{ $t('help.help_page.quick_access.guides.count') }}</div>
          </div>

          <div
            id="support-card"
            class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer"
          >
            <div
              class="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-4"
            >
              <i class="fas fa-headset text-yellow-600 text-xl"></i>
            </div>
            <h3 class="text-lg font-semibold text-accent mb-2">
              {{ $t('help.help_page.quick_access.support.title') }}
            </h3>
            <p class="text-gray-600 text-sm mb-4">
              {{ $t('help.help_page.quick_access.support.description') }}
            </p>
            <div class="text-sm text-gray-500">{{ $t('help.help_page.quick_access.support.availability') }}</div>
          </div>
        </div>
      </div>
    </section>

    <section id="faq-section" class="py-12 bg-white">
      <div class="max-w-7xl mx-auto px-5">
        <h2 class="text-3xl font-bold text-accent mb-8 text-center">
          {{ $t('help.help_page.faq_section.title') }}
        </h2>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div id="developer-faq" class="space-y-4">
            <h3 class="text-xl font-semibold text-primary mb-4">
              {{ $t('help.help_page.faq_section.developer.title') }}
            </h3>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.developer.questions.registration') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
              <div class="px-4 pb-4 text-gray-600 text-sm">
                {{ $t('help.help_page.faq_section.developer.questions.registration_answer') }}
              </div>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.developer.questions.pin_documents') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.developer.questions.pdd_approval') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>
          </div>

          <div id="auditor-faq" class="space-y-4">
            <h3 class="text-xl font-semibold text-secondary mb-4">
              {{ $t('help.help_page.faq_section.auditor.title') }}
            </h3>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.auditor.questions.doe_requirements') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
              <div class="px-4 pb-4 text-gray-600 text-sm">
                {{ $t('help.help_page.faq_section.auditor.questions.doe_requirements_answer') }}
              </div>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.auditor.questions.upload_reports') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium"
                  >{{ $t('help.help_page.faq_section.auditor.questions.conflict_policy') }}</span
                >
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>
          </div>

          <div id="trader-faq" class="space-y-4">
            <h3 class="text-xl font-semibold text-accent mb-4">
              {{ $t('help.help_page.faq_section.trader.title') }}
            </h3>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium">{{ $t('help.help_page.faq_section.trader.questions.transfer_credits') }}</span>
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
              <div class="px-4 pb-4 text-gray-600 text-sm">
                {{ $t('help.help_page.faq_section.trader.questions.transfer_credits_answer') }}
              </div>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium">{{ $t('help.help_page.faq_section.trader.questions.retirement_process') }}</span>
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>

            <div class="border border-gray-200 rounded-lg">
              <button
                class="w-full text-left p-4 hover:bg-gray-50 flex items-center justify-between"
              >
                <span class="font-medium">{{ $t('help.help_page.faq_section.trader.questions.view_ledger') }}</span>
                <i class="fas fa-chevron-down text-gray-400"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="templates-section" class="py-12 bg-gray-50">
      <div class="max-w-7xl mx-auto px-5">
        <h2 class="text-3xl font-bold text-accent mb-8 text-center">
          {{ $t('help.help_page.templates_section.title') }}
        </h2>

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            id="project-templates"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h3 class="text-lg font-semibold text-accent mb-4">
              {{ $t('help.help_page.templates_section.project_documents.title') }}
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-pdf text-red-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.project_documents.pin_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-pdf text-red-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.project_documents.pdd_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-word text-blue-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.project_documents.npramp_form') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>
            </div>
          </div>

          <div
            id="monitoring-templates"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h3 class="text-lg font-semibold text-accent mb-4">
              {{ $t('help.help_page.templates_section.monitoring.title') }}
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-excel text-green-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.monitoring.rmr_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-excel text-green-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.monitoring.sdr_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-pdf text-red-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.monitoring.reversal_form') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>
            </div>
          </div>

          <div
            id="compliance-templates"
            class="bg-white rounded-lg border border-gray-200 p-6"
          >
            <h3 class="text-lg font-semibold text-accent mb-4">
              {{ $t('help.help_page.templates_section.compliance.title') }}
            </h3>
            <div class="space-y-3">
              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-word text-blue-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.compliance.ehmf_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-word text-blue-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.compliance.spcp_template') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>

              <div
                class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
              >
                <div class="flex items-center space-x-3">
                  <i class="fas fa-file-pdf text-red-500"></i>
                  <span class="text-sm font-medium">{{ $t('help.help_page.templates_section.compliance.fpic_form') }}</span>
                </div>
                <i class="fas fa-download text-gray-400"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="support-ticket-section" class="py-12 bg-white">
      <div class="max-w-4xl mx-auto px-5">
        <div class="text-center mb-8">
          <h2 class="text-3xl font-bold text-accent mb-4">
            {{ $t('help.help_page.support_ticket.title') }}
          </h2>
          <p class="text-lg text-gray-600">
            {{ $t('help.help_page.support_ticket.subtitle') }}
          </p>
        </div>

        <div
          id="support-form"
          class="bg-gray-50 rounded-lg border border-gray-200 p-8"
        >
          <form class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('help.help_page.support_ticket.form.full_name') }}</label
                >
                <input
                  type="text"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('help.help_page.support_ticket.form.email_address') }}</label
                >
                <input
                  type="email"
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('help.help_page.support_ticket.form.user_role') }}</label
                >
                <select
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>{{ $t('help.help_page.support_ticket.form.roles.project_developer') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.roles.auditor_doe') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.roles.credit_holder_trader') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.roles.government_dna') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.roles.other') }}</option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('help.help_page.support_ticket.form.priority_level') }}</label
                >
                <select
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option>{{ $t('help.help_page.support_ticket.form.priorities.low') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.priorities.medium') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.priorities.high') }}</option>
                  <option>{{ $t('help.help_page.support_ticket.form.priorities.urgent') }}</option>
                </select>
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('help.help_page.support_ticket.form.subject') }}</label
              >
              <input
                type="text"
                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('help.help_page.support_ticket.form.description') }}</label
              >
              <textarea
                rows="6"
                class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                :placeholder="$t('help.help_page.support_ticket.form.description_placeholder')"
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2"
                >{{ $t('help.help_page.support_ticket.form.attachments') }}</label
              >
              <div
                class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"
              >
                <i
                  class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                ></i>
                <p class="text-gray-600">
                  {{ $t('help.help_page.support_ticket.form.upload_text') }}
                </p>
                <p class="text-sm text-gray-500 mt-1">{{ $t('help.help_page.support_ticket.form.max_file_size') }}</p>
              </div>
            </div>

            <div class="flex justify-end"></div>
          </form>
        </div>
      </div>
    </section>
  </main>
</template>
